import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMemberApp } from '../AppProvider';
import { memberApi } from '../api';
import { memberPath } from '@/config/hosts';
import { IconChat, IconBell, IconPlus } from '../components/Icons';

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useMemberApp();

  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);

  // Form State
  const [postText, setPostText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [contentWarning, setContentWarning] = useState('');
  const [showCwInput, setShowCwInput] = useState(false);

  // Loading / State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [alertNotice, setAlertNotice] = useState(null);

  // Expandable Comments Map
  const [commentsMap, setCommentsComments] = useState({});
  const [newCommentText, setNewCommentText] = useState({});
  const [commentAnonymity, setCommentAnonymity] = useState({});

  useEffect(() => {
    loadGroupData();
  }, [id]);

  const loadGroupData = async () => {
    try {
      setLoading(true);
      setError('');
      const details = await memberApi.communityGroup(id);
      setGroup(details);

      const feed = await memberApi.communityGroupFeed(id);
      setPosts(feed);
    } catch (err) {
      setError('Failed to load group details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!postText.trim()) return;

    try {
      setSubmitting(true);
      setError('');
      setAlertNotice(null);

      const payload = {
        group_id: id,
        text: postText,
        audience: 'group',
        content_warning: contentWarning.trim() || null,
        is_anonymous: isAnonymous,
      };

      const newPost = await memberApi.createCommunityPost(payload);

      if (newPost.crisis_warning) {
        setAlertNotice({
          type: 'crisis',
          message: 'If you are feeling distressed or having thoughts of self-harm, please know you are not alone. You can connect with compassionate, trained professionals immediately at Tele-MANAS (14416) or Kiran (1800-599-0019). We care about your safety.',
        });
      } else if (newPost.moderation_hold) {
        setAlertNotice({
          type: 'hold',
          message: 'Your post contains medical recommendations held for verification. It has been queued for clinician moderation and will appear in public feeds shortly.',
        });
      }

      if (!newPost.moderation_hold) {
        setPosts((prev) => [newPost, ...prev]);
      }

      setPostText('');
      setIsAnonymous(false);
      setContentWarning('');
      setShowCwInput(false);
    } catch (err) {
      setError(err.message || 'Failed to submit post.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleReaction = async (postId, type) => {
    try {
      const result = await memberApi.toggleCommunityPostReaction(postId, { reaction_type: type });
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id === postId) {
            return {
              ...p,
              reactions: result.reactions,
              user_reaction: result.user_reaction,
            };
          }
          return p;
        })
      );
    } catch (err) {
      console.error('Failed to toggle reaction', err);
    }
  };

  const handleLoadComments = async (postId) => {
    try {
      const list = await memberApi.communityComments(postId);
      setCommentsComments((prev) => ({ ...prev, [postId]: list }));
    } catch (err) {
      console.error('Failed to load comments', err);
    }
  };

  const handleAddComment = async (postId) => {
    const text = newCommentText[postId] || '';
    if (!text.trim()) return;

    try {
      const anon = !!commentAnonymity[postId];
      const result = await memberApi.createCommunityComment(postId, {
        text,
        is_anonymous: anon,
      });

      if (result.moderation_hold) {
        alert('Your comment contains treatment advice and is held for review.');
      } else {
        setCommentsComments((prev) => ({
          ...prev,
          [postId]: [...(prev[postId] || []), result],
        }));
      }

      setNewCommentText((prev) => ({ ...prev, [postId]: '' }));
    } catch (err) {
      alert('Failed to post comment.');
    }
  };

  if (loading) {
    return (
      <div className="by-stage">
        <div className="by-empty" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="by-thinking" aria-label="Loading"><span /><span /><span /></div>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="by-stage max-w-4xl mx-auto p-8 text-center text-slate-500">
        Group not found. <button onClick={() => navigate(memberPath('/community'))} className="text-indigo-600 font-bold underline">Go Back to Feed</button>
      </div>
    );
  }

  return (
    <div className="by-stage max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(memberPath('/community'))}
        className="text-xs font-bold text-indigo-600 hover:underline flex items-center gap-1"
      >
        ← Back to Global Feed
      </button>

      {/* Group Info Header */}
      <header className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900"># {group.name}</h1>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-2xl">{group.description}</p>
        </div>
        <span className="bg-indigo-50 text-indigo-700 text-xs font-bold px-3.5 py-1.5 rounded-full border border-indigo-100 whitespace-nowrap">
          {group.category || 'Disease Support'}
        </span>
      </header>

      {/* Warning notices */}
      {alertNotice && (
        <div className={`p-5 rounded-2xl border flex flex-col gap-3 ${
          alertNotice.type === 'crisis' ? 'bg-rose-50 border-rose-200 text-rose-900' : 'bg-amber-50 border-amber-200 text-amber-900'
        }`}>
          <div className="font-bold flex items-center gap-2"><IconBell /> Alert Notice</div>
          <p className="text-sm leading-relaxed">{alertNotice.message}</p>
          <button type="button" onClick={() => setAlertNotice(null)} className="text-xs font-bold text-left underline">Dismiss Notice</button>
        </div>
      )}

      {/* Composer Card */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <form onSubmit={handleCreatePost} className="space-y-4">
          <textarea
            value={postText}
            onChange={(e) => setPostText(e.target.value)}
            placeholder={`Post to #${group.name}...`}
            rows={3}
            maxLength={5000}
            className="w-full p-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-800 text-sm resize-none"
            required
          />

          {showCwInput && (
            <input
              type="text"
              value={contentWarning}
              onChange={(e) => setContentWarning(e.target.value)}
              placeholder="Content Warning (e.g. Severe symptoms)"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          )}

          <div className="flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer font-medium hover:text-slate-800">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Post Anonymously
              </label>
              <button
                type="button"
                onClick={() => setShowCwInput(!showCwInput)}
                className="font-medium hover:text-slate-800 flex items-center gap-1"
              >
                <IconPlus /> {showCwInput ? "Remove CW" : "Add Content Warning"}
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting || !postText.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-xl transition"
            >
              {submitting ? 'Sharing...' : 'Publish'}
            </button>
          </div>
        </form>
      </div>

      {/* Scoped Posts Feed */}
      <div className="space-y-4">
        {posts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-100 text-slate-400 text-sm">
            No support posts inside this group yet. Share a comment or tips to get started!
          </div>
        ) : (
          posts.map((post) => {
            const comments = commentsMap[post.id] || [];
            const showComments = commentsMap[post.id] !== undefined;

            return (
              <article key={post.id} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center font-bold text-indigo-700 text-sm">
                      {post.author_avatar ? (
                        <img src={post.author_avatar} alt={post.author_name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        post.author_name[0]
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-900">{post.author_name}</span>
                        {post.author_badges?.map((b) => (
                          <span key={b} className="bg-indigo-50 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-indigo-100">
                            {b}
                          </span>
                        ))}
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                {post.content_warning ? (
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                    <div className="text-xs font-bold text-slate-600 mb-2">⚠️ Content Warning: {post.content_warning}</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{post.text}</p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{post.text}</p>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between border-t border-b border-slate-50 py-3 text-xs font-semibold text-slate-500">
                  <div className="flex items-center gap-2">
                    {["like", "support", "strength", "same_here"].map((type) => {
                      const count = post.reactions[type] || 0;
                      const active = post.user_reaction === type;
                      const labelMap = { like: '👍 Like', support: '❤️ Support', strength: '💪 Strength', same_here: '🤝 Same Here' };

                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleToggleReaction(post.id, type)}
                          className={`px-2.5 py-1.5 rounded-lg transition hover:bg-slate-50 ${
                            active ? 'text-indigo-600 bg-indigo-50 border border-indigo-100' : ''
                          }`}
                        >
                          {labelMap[type]} {count > 0 && <span className="ml-1 font-bold">{count}</span>}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => (showComments ? setCommentsComments((prev) => {
                      const copy = { ...prev };
                      delete copy[post.id];
                      return copy;
                    }) : handleLoadComments(post.id))}
                    className="hover:text-indigo-600 flex items-center gap-1 px-2 py-1 hover:bg-slate-50 rounded-lg transition"
                  >
                    💬 Comments {showComments ? '▲' : '▼'}
                  </button>
                </div>

                {/* Comments */}
                {showComments && (
                  <div className="space-y-4 pt-2 bg-slate-50/50 p-4 rounded-xl border border-slate-50">
                    <div className="space-y-3">
                      {comments.map((c) => (
                        <div key={c.id} className="bg-white p-3.5 rounded-xl border border-slate-100 flex gap-3 text-xs items-start">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500">
                            {c.author_avatar ? (
                              <img src={c.author_avatar} alt={c.author_name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              c.author_name[0]
                            )}
                          </div>
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">{c.author_name}</span>
                              <span className="text-[10px] text-slate-400 ml-auto">{new Date(c.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-slate-700 leading-relaxed">{c.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={newCommentText[post.id] || ''}
                        onChange={(e) => setNewCommentText((prev) => ({ ...prev, [post.id]: e.target.value }))}
                        placeholder="Reply to this thread..."
                        className="flex-1 p-2.5 border border-slate-200 rounded-xl text-xs bg-white focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddComment(post.id)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-4 py-2.5 rounded-xl text-xs transition"
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}
